import { defineStore } from 'pinia';
import { signInAnonymously, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { computed, readonly, ref } from 'vue';
import { auth, onlineUsersRef, updateUserData } from '@/firebase-config';
import { remove } from 'firebase/database';

const googleAuthProvider = new GoogleAuthProvider();

export const useAuthStore = defineStore('authentication', () => {
  const currentUser = ref<User | null>(auth.currentUser);

  // States for pending auth requests
  const authPending = ref({
    googleSignIn: false,
    anonymousSignIn: false,
  });

  auth.onAuthStateChanged((user) => {
    currentUser.value = user;
  });

  const anonymousSignIn = async () => {
    authPending.value.anonymousSignIn = true;
    await signInAnonymously(auth)
      .then((res) => updateUserData(res.user))
      .finally(() => (authPending.value.anonymousSignIn = false));
  };

  const googleSignIn = async () => {
    authPending.value.googleSignIn = true;

    if (currentUser.value?.isAnonymous) {
      await remove(presenceRef.value);
    }

    await signInWithPopup(auth, googleAuthProvider)
      .then((res) => updateUserData(res.user))
      .finally(() => (authPending.value.googleSignIn = false));
  };

  const presenceRef = computed(() => onlineUsersRef(currentUser.value?.uid));

  const logout = async () => {
    await remove(presenceRef.value);
    await signOut(auth);
  };

  const waitAuthReady = async () => {
    await auth.authStateReady();
  };

  return {
    currentUser: readonly(currentUser),
    authPending,
    anonymousSignIn,
    googleSignIn,
    logout,
    isPending: computed(() => authPending.value.googleSignIn || authPending.value.anonymousSignIn),
    waitAuthReady,
  };
});
